import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
});

// Interceptor: agrega token JWT a cada request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Interceptor: si 401, redirige a login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthRoute = error.config?.url?.includes('/auth/login') ||
                        error.config?.url?.includes('/auth/register');
    if (error.response?.status === 401 && !isAuthRoute) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ===== Auth =====
export const authService = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changeTempPassword: (data) => api.put('/auth/change-temp-password', data),
};

// ===== Assemblies =====
export const assemblyService = {
  getAll: (params) => api.get('/assemblies', { params }),
  getById: (id) => api.get(`/assemblies/${id}`),
  getNextNumber: (propertyId) => api.get('/assemblies/next-number', { params: { property_id: propertyId } }),
  verificarUnidades: (propertyId) => api.get('/assemblies/units-check', { params: { property_id: propertyId } }),
  create: (data) => api.post('/assemblies', data),
  update: (id, data) => api.put(`/assemblies/${id}`, data),
  updateStatus: (id, status) => api.patch(`/assemblies/${id}/status`, { status }),
  setOrdenDelDia: (id, orden_del_dia) => api.patch(`/assemblies/${id}/orden-del-dia`, { orden_del_dia }),
  startMeeting: (id) => api.patch(`/assemblies/${id}/meeting-start`),
  endMeeting:   (id) => api.patch(`/assemblies/${id}/meeting-end`),
  getMeetingToken: (id) => api.get(`/assemblies/${id}/meeting-token`),
  // F-17 (a): verificación de cuórum (solo informa, no bloquea)
  verificarCuorum: (id) => api.post(`/assemblies/${id}/verificar-cuorum`),
  cuorum:          (id) => api.get(`/assemblies/${id}/cuorum`),
  delete: (id) => api.delete(`/assemblies/${id}`),
  // Anexos (documentos de la asamblea)
  documents:       (id) => api.get(`/assemblies/${id}/documents`),
  uploadDocument:  (id, formData) => api.post(`/assemblies/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument:  (id, docId) => api.delete(`/assemblies/${id}/documents/${docId}`),
  downloadDocument:(id, docId) => api.get(`/assemblies/${id}/documents/${docId}/download`, { responseType: 'blob' }),
  // Elecciones (Ola 2) — cargos + postulaciones
  positions:          (id) => api.get(`/assemblies/${id}/positions`),
  createPosition:     (id, data) => api.post(`/assemblies/${id}/positions`, data),
  updatePosition:     (id, posId, data) => api.put(`/assemblies/${id}/positions/${posId}`, data),
  deletePosition:     (id, posId) => api.delete(`/assemblies/${id}/positions/${posId}`),
  positionStatus:     (id, posId, action) => api.post(`/assemblies/${id}/positions/${posId}/status`, { action }),
  addCandidate:       (id, posId, data) => api.post(`/assemblies/${id}/positions/${posId}/candidates`, data),
  setNominationStatus:(id, posId, nomId, status) => api.patch(`/assemblies/${id}/positions/${posId}/nominations/${nomId}`, { status }),
  deleteNomination:   (id, posId, nomId) => api.delete(`/assemblies/${id}/positions/${posId}/nominations/${nomId}`),
  openElection:       (id, posId) => api.post(`/assemblies/${id}/positions/${posId}/open-election`),
  closeElection:      (id, posId) => api.post(`/assemblies/${id}/positions/${posId}/close-election`),
  nominate:           (id, posId, data) => api.post(`/assemblies/${id}/positions/${posId}/nominate`, data),
  withdrawNomination: (id, posId) => api.delete(`/assemblies/${id}/positions/${posId}/nominate`),
};

// ===== Comunicados (Ola 3) =====
export const announcementService = {
  getAll:      (params) => api.get('/announcements', { params }),
  create:      (data) => api.post('/announcements', data),
  update:      (id, data) => api.put(`/announcements/${id}`, data),
  publish:     (id, email) => api.post(`/announcements/${id}/publish`, { email: !!email }),
  unpublish:   (id) => api.post(`/announcements/${id}/unpublish`),
  delete:      (id) => api.delete(`/announcements/${id}`),
  unreadCount: () => api.get('/announcements/unread-count'),
  markRead:    (id) => api.post(`/announcements/${id}/read`),
  markAllRead: () => api.post('/announcements/read-all'),
};

// ===== Glosario (Ola 3) =====
export const glossaryService = {
  getAll: (params) => api.get('/glossary', { params }),
  create: (data) => api.post('/glossary', data),
  update: (id, data) => api.put(`/glossary/${id}`, data),
  delete: (id) => api.delete(`/glossary/${id}`),
};

// ===== Properties =====
export const propertyService = {
  getAll:   (params) => api.get('/properties', { params }),
  // Lista LIGERA para el buscador de conjuntos: { search, limit (1-200), id }. Mismo alcance que getAll.
  selector: (params) => api.get('/properties/selector', { params }),
  dashboardSummary: () => api.get('/properties/dashboard-summary'),
  dashboardResumen: () => api.get('/properties/dashboard-resumen'), // super_admin: resumen exacto de membresías
  getOne:   (id) => api.get(`/properties/${id}`),
  getById:  (id) => api.get(`/properties/${id}`),
  checkNit: (nit, excludeId) => api.get('/properties/check-nit', { params: { nit, exclude_id: excludeId || '' } }),
  create:   (data) => api.post('/properties', data),
  setup:    (data) => api.post('/properties/setup', data),
  update:   (id, data) => api.put(`/properties/${id}`, data),
  delete:   (id) => api.delete(`/properties/${id}`),
};

// ===== Users =====
export const userService = {
  getAll: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// ===== Guests =====
export const guestService = {
  getAll:      (params)       => api.get('/guests', { params }),
  create:      (data)         => api.post('/guests', data),
  update:      (id, data)     => api.put(`/guests/${id}`, data),
  delete:      (id)           => api.delete(`/guests/${id}`),
  template:    ()             => api.get('/guests/template', { responseType: 'blob' }),
  import:      (formData)     => api.post('/guests/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  checkin:     (id, eventId)  => api.post(`/guests/${id}/checkin`, { event_id: eventId }),
  undoCheckin: (id, eventId)  => api.delete(`/guests/${id}/checkin`, { params: { event_id: eventId } }),
  search:      (eventId, q)   => api.get(`/guests/search/${eventId}`, { params: { q } }),
  checkinByQr: (payload)      => api.post('/guests/checkin-by-qr', payload),
  downloadReport:    (eventId) => api.get(`/guests/report/${eventId}/download`, { responseType: 'blob' }),
  downloadReportPdf: (eventId) => api.get(`/guests/report/${eventId}/pdf`, { responseType: 'blob' }),
};

// ===== Questions =====
export const questionService = {
  getAll: (params) => api.get('/questions', { params }),
  create: (data) => api.post('/questions', data),
  update: (id, data) => api.put(`/questions/${id}`, data),
  activate: (id) => api.post(`/questions/${id}/activate`),
  deactivate: (id) => api.post(`/questions/${id}/deactivate`),
  delete: (id) => api.delete(`/questions/${id}`),
};

// ===== Companies =====
export const companyService = {
  getAll: (params) => api.get('/companies', { params }),
  create: (data)   => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id)     => api.delete(`/companies/${id}`),
};

// ===== Events =====
export const eventService = {
  getAll: (params) => api.get('/events', { params }),
  create: (data)   => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id)     => api.delete(`/events/${id}`),
  invite: (id, data) => api.post(`/events/${id}/invite`, data),
};

// ===== Attendances =====
export const attendanceService = {
  getAll:     (params) => api.get('/attendances', { params }),
  myStatus:   (assemblyId) => api.get(`/attendances/my-status/${assemblyId}`),
  // confirm ahora exige el consentimiento de tratamiento de datos
  confirm:    (assemblyId, consent) => api.post(`/attendances/confirm/${assemblyId}`, consent || {}),
  // latido de presencia — lo llama el poll de 5s del dashboard del propietario
  heartbeat:  (assemblyId) => api.post(`/attendances/heartbeat/${assemblyId}`),
  // el propietario se retira (botón "Salir" o cierre de pestaña)
  leave:      (assemblyId) => api.post(`/attendances/leave/${assemblyId}`),
  // unidades que este propietario representa por poder en esta asamblea
  myRepresentations: (assemblyId) => api.get(`/attendances/my-representations/${assemblyId}`),
  // el propietario elige cómo participa: 'virtual' | 'inplace'
  setMode:    (assemblyId, mode) => api.patch(`/attendances/my-mode/${assemblyId}`, { mode }),
  registerRepresentative: (id, formData) => api.post(`/attendances/representative/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeRepresentative:   (id, unidades) => api.delete(`/attendances/representative/${id}`, unidades ? { params: { unidades: unidades.join(',') } } : undefined),
  powerInfo:              (assemblyId) => api.get('/attendances/power-info', { params: { assembly_id: assemblyId } }),
  update:     (id, data) => api.put(`/attendances/${id}`, data),
  delete:     (id) => api.delete(`/attendances/${id}`),
};

// ===== Buildings =====
export const buildingService = {
  getAll: (params) => api.get('/buildings', { params }),
  create: (data) => api.post('/buildings', data),
  update: (id, data) => api.put(`/buildings/${id}`, data),
  delete: (id) => api.delete(`/buildings/${id}`),
};

// ===== Units =====
export const unitService = {
  getAll: (params) => api.get('/units', { params }),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
};

// ===== Votes =====
export const voteService = {
  vote: (questionId, vote) => api.post(`/votes/${questionId}/vote`, { vote }),
  results: (assemblyId) => api.get(`/votes/results/${assemblyId}`),
  myVotes: (assemblyId) => api.get(`/votes/my/${assemblyId}`),
  activeAssembly: (params) => api.get('/votes/active-assembly', { params }),
};

// ===== Owners =====
export const ownerService = {
  getAll:  (params) => api.get('/owners', { params }),
  import:  (formData) => api.post('/owners/import', formData),
  // Reenviar el correo de activación. scope: 'all' | 'unactivated' | 'selected'
  resendWelcome: (data) => api.post('/owners/resend-welcome', data),
  // La unidad pasa a otra persona (venta). Ver ownersController.js · traspasarUnidad.
  traspasarUnidad: (data) => api.post('/owners/traspasar-unidad', data),
  // Corregir los datos de UNA persona (la cédula no cambia). Ver ownersController.js · actualizar.
  update: (id, data) => api.put(`/owners/${id}`, data),
  // "Eliminar" = quitar al propietario de ESTA propiedad puntual (no borra a
  // la persona) — property_id es obligatorio, ver owners.js DELETE /:id.
  delete:  (id, propertyId) => api.delete(`/owners/${id}`, { params: { property_id: propertyId } }),
};

// ===== Property Admins (Super Admin) =====
export const propertyAdminService = {
  getAll:  () => api.get('/property-admins'),
  create:  (data) => api.post('/property-admins', data),          // crear usuario nuevo + asignar
  assign:  (data) => api.post('/property-admins/assign', data),   // asignar usuario existente
  update:  (id, data) => api.put(`/property-admins/${id}`, data),
  delete:  (id) => api.delete(`/property-admins/${id}`),
};

export const membershipService = {
  getAll:        (params) => api.get('/memberships', { params }),
  update:        (id, data) => api.put(`/memberships/${id}`, data),
  renew:         (id, data) => api.post(`/memberships/${id}/renew`, data),
  activarAsamblea:  (id) => api.post(`/memberships/${id}/plan-asamblea`),
  activarLogistica: (id) => api.post(`/memberships/${id}/plan-logistica`),
  activarStandard:  (id, days) => api.post(`/memberships/${id}/plan-standard`, { days }),
  activarPremium:   (id, days) => api.post(`/memberships/${id}/plan-premium`, { days }),
  addExtraAssembly: (id, count = 1) => api.post(`/memberships/${id}/add-extra-assembly`, { count }),
};

// ===== Reportes (centro de descargas: actas + reportes de import) =====
export const reportCenterService = {
  getImportReports: () => api.get('/reports/imports'),
  downloadImportReport: (id) => api.get(`/reports/imports/${id}/download`, { responseType: 'blob' }),
  // Estado del padrón (Ola 1.2): cuenta activada / teléfono / asistencia + consentimiento por asamblea
  getPadron:      (params) => api.get('/reports/padron', { params }),
  downloadPadron: (params) => api.get('/reports/padron/download', { params, responseType: 'blob' }),
};

export default api;
